import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { formatSeconds } from "@/format/time";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { Duration, type PartialMessage } from "@bufbuild/protobuf";
import { type OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC, useCallback, useMemo } from "react";

type LifetimeOption = {
    label: string;
    value: number; // seconds
};

type MaximumEnvironmentLifetimeProps = {
    editable?: boolean;
};

export const MaximumEnvironmentLifetime: FC<MaximumEnvironmentLifetimeProps> = ({ editable = true }) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const currentLifetime = useMemo(() => {
        const seconds = organizationPolicies?.maximumEnvironmentLifetime?.seconds;
        return typeof seconds === "bigint" ? Number(seconds) : 0;
    }, [organizationPolicies]);

    const lifetimeOptions: LifetimeOption[] = useMemo(() => {
        const options = [
            { label: "3 days", value: 3 * 24 * 60 * 60 },
            { label: "1 week", value: 7 * 24 * 60 * 60 },
            { label: "2 weeks", value: 14 * 24 * 60 * 60 },
            { label: "1 month", value: 30 * 24 * 60 * 60 },
            { label: "No maximum lifetime", value: 0 },
        ];
        if (!options.some((option) => option.value === currentLifetime)) {
            options.push({ label: formatSeconds(currentLifetime, "long"), value: currentLifetime });
        }

        return options;
    }, [currentLifetime]);

    const handleLifetimeChange = useCallback(
        async (valueString: string) => {
            const value = parseInt(valueString, 10);
            const params: PartialMessage<OrganizationPolicies> = {
                maximumEnvironmentLifetime: new Duration({
                    seconds: BigInt(value),
                    nanos: 0,
                }),
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: "Maximum environment lifetime updated",
                });
            } catch (error) {
                console.error(error);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [updateOrganizationPolicies, toast],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex min-h-[3.5rem] flex-col gap-0.5">
                    <Heading3 className="text-base">Maximum environment lifetime</Heading3>
                    <Text className="text-sm text-content-secondary">
                        Controls the maximum time an environment is allowed to be reused
                    </Text>
                </div>
                <Select
                    name="maximum-environment-lifetime"
                    value={currentLifetime.toString()}
                    onValueChange={handleLifetimeChange}
                    disabled={!editable || updateOrganizationPolicies.isPending}
                >
                    <SelectTrigger loading={false} className="max-w-[315px]" data-testid="max-environment-lifetime-field">
                        <SelectValue placeholder="Select a lifetime duration" />
                    </SelectTrigger>
                    <SelectContent className="w-[var(--radix-select-trigger-width)]">
                        {lifetimeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value.toString()}>
                                <div className="flex w-full items-center justify-between">{option.label}</div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </SkeletonBlock>
        </div>
    );
};
